// Ctrl+K command palette action registry.  Each command is a discoverable
// verb (as opposed to a searchable noun — clients, matters, contacts, etc.).
// Commands appear at the TOP of the search results when the query matches
// their label or keywords, so muscle-memory ("new time entry" → Enter) works
// alongside plain-text search.
//
// Adding a command:
//   { id, label, hint?, icon, keywords: [...], perm?, run: ({ navigate, dispatch, logout }) => {...} }
//
// `perm` gates visibility on the caller's permission map: string means the
// perm must be true; array means any-of; 'admin' means admin-or-is_admin.
// Undefined = visible to everyone.  Admin always sees every command.

const ALL_COMMANDS = [
  // ── CREATE actions — dispatched or ?new=1 param on the target page ──
  {
    id: 'new-time-entry',
    label: 'New time entry',
    hint: 'Open the timer',
    icon: '⏱',
    keywords: ['add', 'log', 'timer', 'time', 'entry'],
    perm: 'can_post_time',
    run: ({ dispatch }) => dispatch('open-time-entry-popup')
  },
  // One click, no form (firm decision 2026-10-13): Layout — or, in a
  // popped-out page, useTimerEventBridge — starts a timer with no matter,
  // picked later from ⏱ Timers.  'New time entry' above stays the form.
  // Listed after it so "time" / "new time…" still put the form first.
  {
    id: 'start-timer',
    label: 'Start timer',
    hint: 'Starts at once — pick the matter later',
    icon: '▶️',
    keywords: ['timer', 'clock', 'begin', 'track', 'stopwatch', 'one click'],
    perm: 'can_post_time',
    run: ({ dispatch }) => dispatch('start-timer')
  },
  {
    id: 'new-matter',
    label: 'New matter',
    icon: '📁',
    keywords: ['create', 'add', 'case', 'file'],
    perm: 'can_create_matters',
    run: ({ navigate }) => navigate('/matters?new=1')
  },
  {
    id: 'new-client',
    label: 'New client',
    icon: '👥',
    keywords: ['create', 'add', 'person'],
    perm: 'can_create_clients',
    run: ({ navigate }) => navigate('/clients?new=1')
  },
  {
    id: 'new-contact',
    label: 'New contact',
    icon: '📇',
    keywords: ['create', 'add', 'witness', 'expert', 'opposing counsel'],
    perm: 'can_create_contacts',
    run: ({ navigate }) => navigate('/contacts?new=1')
  },
  {
    id: 'new-event',
    label: 'New calendar event',
    icon: '📅',
    keywords: ['create', 'add', 'appointment', 'hearing', 'deadline', 'meeting'],
    perm: 'calendar',
    run: ({ navigate }) => navigate('/calendar?new=1')
  },
  {
    id: 'new-sol',
    label: 'New SOL entry',
    icon: '⏳',
    keywords: ['statute', 'limitations', 'deadline', 'create', 'add'],
    perm: 'can_create_sol',
    run: ({ navigate }) => navigate('/sol?new=1')
  },
  {
    id: 'new-invoice',
    label: 'New invoice',
    hint: 'Go to Billing to generate one',
    icon: '💰',
    keywords: ['create', 'bill', 'generate'],
    perm: 'can_create_invoices',
    run: ({ navigate }) => navigate('/billing')
  },
  {
    id: 'batch-invoice',
    label: 'Batch invoicing',
    icon: '📄',
    keywords: ['generate', 'multiple', 'wizard'],
    perm: 'batch_invoicing',
    run: ({ navigate }) => navigate('/batch-invoicing')
  },

  // ── SYSTEM actions ──
  {
    id: 'show-shortcuts',
    label: 'Show keyboard shortcuts',
    icon: '⌨️',
    keywords: ['help', 'keys', 'hotkey', 'shortcut'],
    run: ({ dispatch }) => dispatch('show-shortcuts-help')
  },
  {
    id: 'report-issue',
    label: 'Report an issue',
    hint: 'Send feedback to the admin team',
    icon: '💬',
    keywords: ['bug', 'feedback', 'problem', 'help'],
    run: ({ dispatch }) => dispatch('open-report-issue')
  },
  {
    id: 'sign-out',
    label: 'Sign out',
    icon: '🚪',
    keywords: ['logout', 'log out', 'quit'],
    run: ({ logout }) => logout && logout()
  },

  // ── NAVIGATION shortcuts (go-to-page) — subset of top pages ──
  { id: 'go-dashboard',      label: 'Go to Dashboard',       icon: '🏠', keywords: ['home'],                          perm: 'dashboard',  run: ({ navigate }) => navigate('/dashboard') },
  { id: 'go-dailies',        label: 'Go to Dailies',         icon: '📋', keywords: ['today', 'snapshot'],             perm: 'dailies',    run: ({ navigate }) => navigate('/dailies') },
  { id: 'go-matters',        label: 'Go to Matters',         icon: '📁', keywords: ['cases', 'files'],                perm: 'matters',    run: ({ navigate }) => navigate('/matters') },
  { id: 'go-clients',        label: 'Go to Clients',         icon: '👥', keywords: [],                                 perm: 'clients',    run: ({ navigate }) => navigate('/clients') },
  { id: 'go-contacts',       label: 'Go to Contacts',        icon: '📇', keywords: [],                                 perm: 'contacts',   run: ({ navigate }) => navigate('/contacts') },
  { id: 'go-time-entries',   label: 'Go to Time Entries',    icon: '⏱', keywords: ['hours', 'log'],                  perm: 'time_entries', run: ({ navigate }) => navigate('/time-entries') },
  { id: 'go-calendar',       label: 'Go to Calendar',        icon: '📅', keywords: ['schedule', 'events'],            perm: 'calendar',   run: ({ navigate }) => navigate('/calendar') },
  { id: 'go-deadlines',      label: 'Go to Deadlines',       icon: '⏰', keywords: ['due', 'agenda'],                 perm: 'calendar',   run: ({ navigate }) => navigate('/deadlines') },
  // "To-Do" is the page's one name (sidebar, quick bar, heading — held equal
  // by lib/todoName.test.js); "tasks" stays a keyword for people who knew it
  // as the Tasks page.  The id and path are unchanged.
  { id: 'go-tasks',          label: 'Go to To-Do',           icon: '✅', keywords: ['tasks', 'task', 'todo', 'to-do', 'to do', 'todos', 'to-do list', 'to do list', 'checklist'], perm: 'tasks',      run: ({ navigate }) => navigate('/tasks') },
  { id: 'go-billing',        label: 'Go to Billing',         icon: '💰', keywords: ['ar', 'wip'],                     perm: 'billing_tab',run: ({ navigate }) => navigate('/billing') },
  { id: 'go-invoices',       label: 'Go to Invoices',        icon: '📄', keywords: [],                                 perm: 'invoices',   run: ({ navigate }) => navigate('/invoices') },
  { id: 'go-receipts',       label: 'Go to Receipts',        icon: '🧾', keywords: ['payment'],                       perm: 'receipts',   run: ({ navigate }) => navigate('/receipts') },
  { id: 'go-trust',          label: 'Go to Trust',           icon: '🏦', keywords: ['iolta', 'ctapp'],                perm: 'trust',      run: ({ navigate }) => navigate('/trust') },
  // Same any-of list as the /reports route (App.js) and the sidebar
  // (lib/navItems.js): since 2026-11-02 anyone with Time Entries or Create &
  // Post Time Entries opens Reports for their own Timesheet (Hestia via James).
  { id: 'go-reports',        label: 'Go to Reports',         icon: '📊', keywords: ['pdf'],                            perm: ['reports', 'time_entries', 'can_post_time'], run: ({ navigate }) => navigate('/reports') },
  { id: 'go-communications', label: 'Go to Communications',  icon: '📞', keywords: ['email', 'phone', 'log'],         perm: 'communications', run: ({ navigate }) => navigate('/communications') },
  { id: 'go-sol',            label: 'Go to SOL Tracker',     icon: '⏳', keywords: ['statute', 'limitations'],        perm: 'sol_tracker',run: ({ navigate }) => navigate('/sol') },
  { id: 'go-discovery',      label: 'Go to Discovery',       icon: '🔍', keywords: ['rogs', 'rfp', 'depo'],           perm: 'matters',    run: ({ navigate }) => navigate('/discovery') },
  { id: 'go-court-rules',    label: 'Go to Court Rules',     icon: '⚖️', keywords: ['deadline', 'calc'],              perm: 'matters',    run: ({ navigate }) => navigate('/court-rules') },
  { id: 'go-conflicts',      label: 'Go to Conflict Checker',icon: '🚫', keywords: ['check', 'search'],               perm: 'conflicts',  run: ({ navigate }) => navigate('/conflicts') },
  // perm 'admin', not 'settings': /settings is <AdminRoute> in App.js, so a
  // non-admin holding the `settings` permission was offered a command that
  // only bounced them to the dashboard.  Feedback #835 (James, 2026-09-30)
  // made the sidebar and the quick bar admin-only for Settings; the palette
  // has to agree.  Guarded in lib/quickBarItems.test.js.
  { id: 'go-settings',       label: 'Go to Firm Settings',   icon: '⚙️', keywords: [],                                 perm: 'admin',      run: ({ navigate }) => navigate('/settings') },
  { id: 'go-profile',        label: 'Go to Profile',         icon: '👤', keywords: ['me', 'account'],                 run: ({ navigate }) => navigate('/profile') },
  { id: 'go-help',           label: 'Go to Help',            icon: '❓', keywords: ['manual', 'docs'],                run: ({ navigate }) => navigate('/help') },

  // ── ADMIN pages ──
  { id: 'go-admin-home',     label: 'Go to Admin Tools',     icon: '🛠️', keywords: ['admin', 'utilities', 'tools'],   perm: 'admin',      run: ({ navigate }) => navigate('/admin') },
  { id: 'go-team',           label: 'Go to Team Management', icon: '👨‍💼', keywords: ['users', 'permissions'], perm: 'team',       run: ({ navigate }) => navigate('/team') },
  { id: 'go-audit-log',      label: 'Go to Audit Log',       icon: '📜', keywords: ['history'],                       perm: 'admin',      run: ({ navigate }) => navigate('/audit-log') },
  { id: 'go-feedback',       label: 'Go to Feedback Inbox',  icon: '💬', keywords: ['reports', 'issues'],             perm: 'admin',      run: ({ navigate }) => navigate('/feedback') },
  { id: 'go-import',         label: 'Go to Import',          icon: '📥', keywords: ['migration', 'amicus', 'bak'],    perm: 'admin',      run: ({ navigate }) => navigate('/import') },
  { id: 'go-client-merge',   label: 'Merge duplicate clients', icon: '🔀', keywords: ['dedupe'],                       perm: 'can_delete_clients', run: ({ navigate }) => navigate('/clients/merge') },
  { id: 'go-time-dup',       label: 'Time Entry Duplicate Resolver', icon: '👯', keywords: ['dedupe'],                perm: 'admin',      run: ({ navigate }) => navigate('/time-entry-duplicates') },
  { id: 'go-downloads',      label: 'Go to Downloads',       icon: '💻', keywords: ['desktop', 'app'],                perm: 'downloads',  run: ({ navigate }) => navigate('/downloads') },
]

function hasPerm(user, perm) {
  if (!perm) return true
  const isAdmin = user?.role === 'admin' || user?.permissions?.is_admin === true
  if (isAdmin) return true
  if (perm === 'admin') return false
  if (Array.isArray(perm)) return perm.some((p) => user?.permissions?.[p] === true)
  return user?.permissions?.[perm] === true
}

// Public: return commands visible to this user, in registry order.
export function listCommands(user) {
  return ALL_COMMANDS.filter((c) => hasPerm(user, c.perm))
}

// Score a command against a query.  Higher = better match.  0 = no match.
// Simple substring scoring with a small bonus for label-start-of-word matches
// so typing "new mat" ranks "New matter" above "Matter Status Report".
export function scoreCommand(cmd, query) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return 0
  const label = cmd.label.toLowerCase()
  const keywords = (cmd.keywords || []).map((k) => k.toLowerCase())

  // Exact label prefix match — biggest bonus
  if (label.startsWith(q)) return 100 - Math.min(50, label.length)

  // Word-boundary match in label
  const words = label.split(/\s+/)
  if (words.some((w) => w.startsWith(q))) return 60

  // Substring in label
  if (label.includes(q)) return 40

  // Match every space-separated query token against label + keywords
  const tokens = q.split(/\s+/).filter(Boolean)
  const haystack = label + ' ' + keywords.join(' ')
  const hits = tokens.filter((t) => haystack.includes(t)).length
  if (hits === tokens.length) return 20 + hits * 2
  if (hits > 0) return 10

  return 0
}

// Top N commands matching the query, sorted by score.
export function matchCommands(user, query, limit = 5) {
  const cmds = listCommands(user)
  const scored = cmds
    .map((c) => ({ cmd: c, score: scoreCommand(c, query) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
  return scored.map((x) => x.cmd)
}
